import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import InputSection from '@/components/General/inputs/InputSection';
import CareDetailsFields from '@/components/FollowUp/Care/CareDetailsFields';
import { Mutation } from '@/utils/react-query/Mutations';
import { buildCareDetailsPatch, toMealDateKey } from '@/utils/followUpCare';

const EMPTY_DETAILS = {
    location: '',
    locationInstructions: '',
    visitPreferences: '',
    coordinationContact: '',
    deliveryInstructions: '',
    reviewDate: null,
};

/** 'YYYY-MM-DD' stored key → local Date for the picker (null-safe). */
const dateKeyToDate = (key) => {
    if (!key) return null;
    const d = new Date(`${key}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * EditCareDetailsSheet — in-place edits to an ACTIVE care episode's
 * careDetails via follow_up_update_care_details (spec §7.5/§25.9). The
 * server merge-patches (RFC 7396): the submit diffs stored values against
 * the form and sends ONLY the changed keys — a cleared field serializes
 * as null so the stored key is deleted.
 *
 * Visible only to 'Manage Follow Up' or the person's primary caregiver
 * (the follow_up_close_care gate); the caller also hides it for non-ACTIVE
 * episodes — closed/cancelled episodes are retained history (C15).
 */
const EditCareDetailsSheet = ({ isOpen, onClose, organizationID, careEpisodeID, personID, personName, careDetails }) => {
    const [details, setDetails] = useState(EMPTY_DETAILS);

    // Seed from the stored details each time the sheet opens.
    useEffect(() => {
        if (!isOpen) return;
        setDetails({
            location: careDetails?.location ?? '',
            locationInstructions: careDetails?.locationInstructions ?? '',
            visitPreferences: careDetails?.visitPreferences ?? '',
            coordinationContact: careDetails?.coordinationContact ?? '',
            deliveryInstructions: careDetails?.deliveryInstructions ?? '',
            reviewDate: dateKeyToDate(careDetails?.reviewDate),
        });
    }, [isOpen, careDetails]);

    const updateMutation = Mutation.followUp.updateCareDetails({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Care details updated' });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to update care details. Please try again.' });
            }
        },
    });

    const submitting = updateMutation.isPending;

    const handleSubmit = () => {
        if (submitting || !careEpisodeID) return;
        const patch = buildCareDetailsPatch(careDetails || {}, {
            ...details,
            reviewDate: details.reviewDate ? toMealDateKey(details.reviewDate) : '',
        });
        if (Object.keys(patch).length === 0) {
            // Nothing changed — a no-op RPC is valid but pointless.
            onClose?.();
            return;
        }
        updateMutation.mutate({ organizationID, careEpisodeID, personID, patch });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Edit Care Details"
            subtitle={personName ? `for ${personName}` : undefined}
            snapPoints={[520, 720]}
            cancelText="Cancel"
            confirmText="Save"
            onConfirm={submitting ? null : handleSubmit}
            loading={submitting}
        >
            <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
                <InputSection
                    title="Care details"
                    description="All optional. Clearing a field removes it from the episode."
                >
                    <CareDetailsFields
                        values={details}
                        onFieldChange={(key, value) => setDetails((d) => ({ ...d, [key]: value }))}
                        testIDPrefix="edit-care-details"
                        disabled={submitting}
                    />
                </InputSection>
                <View style={styles.bottomPad} />
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(() => ({
    scroll: { flexGrow: 0 },
    bottomPad: { height: 16 },
}));

export default EditCareDetailsSheet;
